/**
 * @module TTProductMainInfoCustom
 * Injects the custom PDP info view into the theme column (class or data-view).
 *
 * Preserves theme nodes `.product-details-quantity` and `.product-details-full-actions` (detached
 * before the slot is cleared, then re-appended after the custom view so SuiteCommerce controls stay intact).
 *
 * Does not depend on the AMD module "jQuery" (some builds fail to resolve it and the whole
 * extension would not load). DOM APIs only for the slot; Backbone still uses global jQuery for $el.
 */
define('TTProductMainInfoCustom'
,	[
		'TTProductMainInfoCustom.View'
	]
,	function (
		TTProductMainInfoCustomView
	)
{
	'use strict';

	var CONTENT_ROOT_SELECTORS = [
		'#content'
	,	'.shopping-layout-content'
	];

	/** Known non-PDP roots inside the main content area (Tavano theme + legacy SC ids). */
	var NON_PDP_CONTENT_SELECTORS = [
		'.home'
	,	'.facets-facet-browse'
	,	'.cart-detailed'
	,	'.cart-summary'
	,	'.cms-landing-page'
	,	'[data-view="TavanoTeam.MasterTheme.Home.Extension.View"]'
	,	'#home-page'
	,	'#facet-browse'
	,	'#cart'
	];

	/**
	 * Selectors relative to `.product-details-full` root.
	 */
	var SLOT_SELECTORS = [
		'.product-details-full-main-content-right'
	,	'[data-view="ProductDetails.Information.View"]'
	];

	function getContentRoot ()
	{
		var i;
		var el;
		for (i = 0; i < CONTENT_ROOT_SELECTORS.length; i++)
		{
			el = document.querySelector(CONTENT_ROOT_SELECTORS[i]);
			if (el)
			{
				return el;
			}
		}
		return null;
	}

	function isNonPdpViewActive ()
	{
		var content = getContentRoot();
		var i;

		if (!content)
		{
			return true;
		}

		for (i = 0; i < NON_PDP_CONTENT_SELECTORS.length; i++)
		{
			if (content.querySelector(NON_PDP_CONTENT_SELECTORS[i]))
			{
				return true;
			}
		}

		// Active content has no full PDP root — home, PLP, cart, CMS, etc.
		if (!content.querySelector('.product-details-full'))
		{
			return true;
		}

		return false;
	}

	function hasPdpSlotContainer (root)
	{
		var i;
		var el;

		if (!root)
		{
			return false;
		}

		for (i = 0; i < SLOT_SELECTORS.length; i++)
		{
			try
			{
				el = root.querySelector(SLOT_SELECTORS[i]);
			}
			catch (eSlot)
			{
				el = null;
			}
			if (el)
			{
				return true;
			}
		}

		return false;
	}

	function getFullPdpRoot ()
	{
		var content;
		var root;

		if (isNonPdpViewActive())
		{
			return null;
		}

		content = getContentRoot();
		if (!content)
		{
			return null;
		}

		root = content.querySelector('.product-details-full');
		if (!root || root.closest('.product-details-quickview'))
		{
			return null;
		}

		return root;
	}

	/**
	 * Full PDP only — not quick view, home, PLP, cart, CMS landings, etc.
	 */
	function isFullPdpPage (container)
	{
		var pdp;
		var info;
		var root;

		if (!container || typeof container.getComponent !== 'function')
		{
			return false;
		}

		root = getFullPdpRoot();
		if (!root || !hasPdpSlotContainer(root))
		{
			return false;
		}

		pdp = container.getComponent('PDP');
		if (!pdp || typeof pdp.getItemInfo !== 'function')
		{
			return false;
		}

		info = pdp.getItemInfo();
		return !!(info && (info.item || (info.line && info.line.item)));
	}

	var RETRY_DELAYS_MS = [0, 50, 150, 400, 1000, 2000];

	var POLL_INTERVAL_MS = 250;

	var POLL_MAX_MS = 12000;

	var viewInstance = null;

	var pollTimerId = null;

	var pollDeadline = 0;

	/**
	 * Detach theme PDP blocks that must not be replaced. Returns nodes in document order.
	 */
	function detachPreservedThemeBlocks (root)
	{
		var q = root.querySelector('.product-details-quantity');
		var a = root.querySelector('.product-details-full-actions');
		if (!q && !a)
		{
			return [];
		}
		var nodes;
		if (q && a && q.contains(a))
		{
			nodes = [q];
		}
		else if (q && a && a.contains(q))
		{
			nodes = [a];
		}
		else
		{
			nodes = [];
			if (q)
			{
				nodes.push(q);
			}
			if (a)
			{
				nodes.push(a);
			}
			nodes.sort(function (x, y) {
				var pos = x.compareDocumentPosition(y);
				if (pos & Node.DOCUMENT_POSITION_FOLLOWING)
				{
					return -1;
				}
				if (pos & Node.DOCUMENT_POSITION_PRECEDING)
				{
					return 1;
				}
				return 0;
			});
		}
		var i;
		var n;
		for (i = 0; i < nodes.length; i++)
		{
			n = nodes[i];
			if (n && n.parentNode)
			{
				n.parentNode.removeChild(n);
			}
		}
		return nodes;
	}

	function findSlotElement ()
	{
		var root = getFullPdpRoot();
		if (!root)
		{
			return null;
		}

		var i;
		var el;
		for (i = 0; i < SLOT_SELECTORS.length; i++)
		{
			try
			{
				el = root.querySelector(SLOT_SELECTORS[i]);
			}
			catch (e)
			{
				el = null;
			}
			if (el)
			{
				return { el: el, selector: SLOT_SELECTORS[i] };
			}
		}
		return null;
	}

	function getOrCreateView (container)
	{
		if (!viewInstance)
		{
			viewInstance = new TTProductMainInfoCustomView({
				container: container
			});
		}
		return viewInstance;
	}

	function stopPolling ()
	{
		if (pollTimerId !== null)
		{
			clearInterval(pollTimerId);
			pollTimerId = null;
		}
		pollDeadline = 0;
	}

	function attachAndRender (container)
	{
		if (!isFullPdpPage(container))
		{
			return false;
		}

		try
		{
			var found = findSlotElement();
			if (!found || !found.el)
			{
				return false;
			}

			var slotEl = found.el;
			var view = getOrCreateView(container);

			if (view.el.parentNode !== slotEl)
			{
				var preserved = detachPreservedThemeBlocks(slotEl);
				var pi;
				while (slotEl.firstChild)
				{
					slotEl.removeChild(slotEl.firstChild);
				}
				slotEl.appendChild(view.el);
				for (pi = 0; pi < preserved.length; pi++)
				{
					slotEl.appendChild(preserved[pi]);
				}
			}

			view.render();
			stopPolling();
			return true;
		}
		catch (err)
		{
			return false;
		}
	}

	function scheduleAttachRetries (container)
	{
		var i;
		for (i = 0; i < RETRY_DELAYS_MS.length; i++)
		{
			(function (ms)
			{
				setTimeout(function ()
				{
					if (!isFullPdpPage(container))
					{
						teardownView();
						return;
					}
					attachAndRender(container);
				}, ms);
			}(RETRY_DELAYS_MS[i]));
		}
	}

	function startPolling (container)
	{
		stopPolling();
		pollDeadline = new Date().getTime() + POLL_MAX_MS;
		pollTimerId = setInterval(function ()
		{
			if (!isFullPdpPage(container))
			{
				teardownView();
				return;
			}

			if (new Date().getTime() > pollDeadline)
			{
				stopPolling();
				return;
			}
			attachAndRender(container);
		}, POLL_INTERVAL_MS);
	}

	function teardownView ()
	{
		stopPolling();
		if (viewInstance)
		{
			try
			{
				viewInstance.remove();
			}
			catch (eTeardown)
			{
				/* ignore */
			}
			viewInstance = null;
		}
	}

	function onPdpReady (container)
	{
		if (!isFullPdpPage(container))
		{
			teardownView();
			return;
		}

		if (!attachAndRender(container))
		{
			scheduleAttachRetries(container);
			startPolling(container);
		}
	}

	/**
	 * Add-to-cart often re-renders PDP / mini cart and replaces the info column DOM, detaching our view.
	 * Re-attach after Cart finishes (and retry briefly: some themes update the slot asynchronously).
	 */
	var REATTACH_AFTER_CART_DELAYS_MS = [0, 100, 350, 700];

	function scheduleReattachAfterCart (container)
	{
		if (!isFullPdpPage(container))
		{
			return;
		}

		var i;
		for (i = 0; i < REATTACH_AFTER_CART_DELAYS_MS.length; i++)
		{
			(function (ms) {
				setTimeout(function () {
					try
					{
						if (!isFullPdpPage(container))
						{
							teardownView();
							return;
						}

						var view = getOrCreateView(container);
						var found = findSlotElement();
						if (!found || !found.el)
						{
							onPdpReady(container);
							return;
						}
						if (view.el && view.el.parentNode === found.el)
						{
							/* Still attached: re-render so we pick up PDP when it recovers; View caches context if getItemInfo is briefly empty. */
							view.render();
							return;
						}
						onPdpReady(container);
					}
					catch (eRe)
					{
						/* ignore */
					}
				}, ms);
			}(REATTACH_AFTER_CART_DELAYS_MS[i]));
		}
	}

	return {
		mountToApp: function mountToApp (container)
		{
			try
			{
				if (!container || typeof container.getComponent !== 'function')
				{
					return;
				}

				var pdp = container.getComponent('PDP');
				var layout = container.getComponent('Layout');
				var cart = container.getComponent('Cart');

				function safePdpHandler ()
				{
					try
					{
						if (!isFullPdpPage(container))
						{
							return;
						}
						onPdpReady(container);
					}
					catch (e)
					{
						/* ignore */
					}
				}

				function safeLayoutContentHandler ()
				{
					try
					{
						if (isFullPdpPage(container))
						{
							onPdpReady(container);
						}
						else
						{
							teardownView();
						}
					}
					catch (eLayout)
					{
						/* ignore */
					}
				}

				if (layout && typeof layout.on === 'function')
				{
					layout.on('afterShowContent', safeLayoutContentHandler);
				}

				if (pdp && typeof pdp.on === 'function')
				{
					pdp.on('afterOptionSelection', safePdpHandler);
				}

				if (cart && typeof cart.on === 'function')
				{
					cart.on('afterAddLine', function () {
						scheduleReattachAfterCart(container);
					});
				}
			}
			catch (err)
			{
				/* ignore */
			}
		}
	};
});
